<template>
  <section class="actions" data-cy="membership-actions-card">
    <header class="actions__head">
      <span class="actions__eyebrow">Action Items</span>
      <Tooltip placement="top" :width="280">
        <template #trigger>
          <i
            class="fas fa-info-circle actions__info"
            aria-label="More information"
          ></i>
        </template>
        <template #tooltip>
          <span class="actions__tooltip-text">{{ TOOLTIP }}</span>
        </template>
      </Tooltip>
    </header>

    <div class="actions__body">
      <Skeleton
        v-if="loading"
        width="100%"
        height="6rem"
        data-cy="membership-actions-skeleton"
      />
      <ul v-else class="actions__rows">
        <li
          v-for="row in rows"
          :key="row.key"
          class="actions__row-item"
          :data-cy="`actions-row-${row.key}`"
        >
          <component
            :is="row.href ? 'a' : 'div'"
            :href="row.href"
            class="actions__row"
            :class="{ 'actions__row--linked': row.href }"
          >
            <span class="actions__label">{{ row.label }}</span>
            <span
              class="actions__value"
              :class="`actions__value--${row.severity}`"
              >{{ row.value }}</span
            >
          </component>
        </li>
      </ul>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from "vue";

import Tooltip from "@/components/Tooltip.vue";
import { type ActionSeverity, severityForAction } from "./severity";
import Skeleton from "./Skeleton.vue";
import { useMembershipDashboard } from "./useMembershipDashboard";

const TOOLTIP =
  "Open work items in your queue. Click any row to jump to the relevant tab.";

const numberFormatter = new Intl.NumberFormat("en-US");

const { summary, loading, membershipsTabHref } = useMembershipDashboard();

interface Row {
  key: string;
  label: string;
  count: number;
  value: string;
  severity: ActionSeverity;
  href: string | undefined;
}

const rows = computed<Row[]>(() => {
  const todoCount = summary.value?.memberships_todo_count ?? 0;
  const unusedCount = summary.value?.unused_services_count ?? 0;

  return [
    {
      key: "memberships-todo",
      label: "Memberships to review",
      count: todoCount,
      value: numberFormatter.format(todoCount),
      severity: severityForAction(todoCount),
      href: membershipsTabHref("Membership To-Do")
    },
    {
      key: "unused-services",
      label: "Members with available services",
      count: unusedCount,
      value: numberFormatter.format(unusedCount),
      severity: severityForAction(unusedCount),
      // Suffix the Service Utilization tab URL with the Available status
      // filter so the datatable's distinct-customer aggregate matches this
      // tile's value, which counts only customers with at least one unused
      // benefit slot in the current year.
      href: (() => {
        const base = membershipsTabHref("Membership Service Utilization");
        return base ? `${base}&status=Available` : undefined;
      })()
    }
  ];
});
</script>

<style lang="scss" scoped>
.actions {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background-color: #fff;
  border: 1px solid var(--medium-color, #e5e7eb);
  border-radius: 6px;
  padding: 1.25rem 1.5rem;
  height: 100%;
}
.actions__head {
  display: flex;
  align-items: center;
  gap: 0.375rem;
}
.actions__eyebrow {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #4b5563;
}
.actions__info {
  font-size: 0.75rem;
  color: #9ca3af;
  cursor: help;
  &:hover {
    color: #4b5563;
  }
}
.actions__tooltip-text {
  font-size: 0.8125rem;
  line-height: 1.4;
}
.actions__body {
  flex: 1;
}
.actions__rows {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
}
.actions__row-item + .actions__row-item {
  border-top: 1px solid #f3f4f6;
}
.actions__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 0;
  text-decoration: none;
  color: inherit;
}
.actions__row--linked {
  cursor: pointer;
  transition: background-color 120ms ease;
  margin: 0 -0.5rem;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  border-radius: 4px;
  &:hover {
    background-color: #f9fafb;
  }
}
.actions__label {
  font-size: 0.875rem;
  color: #374151;
}
.actions__value {
  font-size: 1.5rem;
  font-weight: 600;
  color: #111827;
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum";
}
.actions__value--caution {
  color: #b45309;
}
</style>
